import React, { useMemo, useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Filter, ChevronDown, Grid3x3, List, Home } from 'lucide-react';
import ProductCard from '../components/ProductCard';
import { useProducts } from '../context/ProductsContext';

const Shop = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { products, categories } = useProducts();
  const q = searchParams.get('q') || '';
  const [sort, setSort] = useState('default');
  const [view, setView] = useState('grid');
  const [priceMax, setPriceMax] = useState(4000);

  const category = slug ? categories.find((c) => c.slug === slug) : null;

  const filtered = useMemo(() => {
    let list = products;
    if (slug) list = list.filter((p) => p.category === slug);
    if (q) list = list.filter((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    list = list.filter((p) => p.price <= priceMax);

    switch (sort) {
      case 'price-asc':
        list = [...list].sort((a, b) => a.price - b.price);
        break;
      case 'price-desc':
        list = [...list].sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        list = [...list].reverse();
        break;
      default:
        break;
    }
    return list;
  }, [slug, q, sort, priceMax, products]);

  const title = category ? category.name : q ? `Rezultate pentru "${q}"` : 'Magazin';

  return (
    <div className="bg-white min-h-screen">
      {/* Breadcrumb */}
      <div className="bg-neutral-50 border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 py-3 text-sm flex items-center gap-2 text-neutral-500">
          <Link to="/" className="hover:text-amber-600 flex items-center gap-1">
            <Home className="w-3.5 h-3.5" /> Acasă
          </Link>
          <span>/</span>
          <Link to="/magazin" className="hover:text-amber-600">Magazin</Link>
          {category && (
            <>
              <span>/</span>
              <span className="text-neutral-900">{category.name}</span>
            </>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-neutral-900">{title}</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <aside className="lg:col-span-1 space-y-6">
            <div className="bg-white border border-neutral-200 rounded-2xl p-5">
              <h3 className="font-semibold text-neutral-900 mb-4 flex items-center gap-2">
                <Filter className="w-4 h-4" /> Categorii
              </h3>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link
                    to="/magazin"
                    className={`flex items-center justify-between py-1.5 hover:text-amber-600 ${
                      !slug ? 'text-amber-700 font-medium' : 'text-neutral-700'
                    }`}
                  >
                    <span>Toate produsele</span>
                    <span className="text-xs text-neutral-400">({products.length})</span>
                  </Link>
                </li>
                {categories.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/categorie/${c.slug}`}
                      className={`flex items-center justify-between py-1.5 hover:text-amber-600 ${
                        slug === c.slug ? 'text-amber-700 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      <span>{c.name}</span>
                      <span className="text-xs text-neutral-400">({c.count})</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white border border-neutral-200 rounded-2xl p-5">
              <h3 className="font-semibold text-neutral-900 mb-4">Filtru de preț</h3>
              <input
                type="range"
                min="500"
                max="4000"
                step="100"
                value={priceMax}
                onChange={(e) => setPriceMax(Number(e.target.value))}
                className="w-full accent-amber-500"
              />
              <div className="flex items-center justify-between text-xs text-neutral-600 mt-2">
                <span>500 lei</span>
                <span className="font-semibold text-neutral-900">Până la {priceMax} lei</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 to-amber-100 border border-amber-200 rounded-2xl p-5">
              <h4 className="font-serif font-bold text-amber-900 text-lg">Ai nevoie de ajutor?</h4>
              <p className="text-sm text-amber-800 mt-1">Echipa noastră este aici pentru tine.</p>
              <Link
                to="/contact"
                className="inline-block mt-3 text-sm font-medium text-amber-700 hover:text-amber-900 underline"
              >
                Contactează-ne
              </Link>
            </div>
          </aside>

          {/* Main */}
          <main className="lg:col-span-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6 p-4 bg-neutral-50 rounded-xl border border-neutral-200">
              <span className="text-sm text-neutral-600">
                Afișez {filtered.length} {filtered.length === 1 ? 'rezultat' : 'rezultate'}
              </span>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 border border-neutral-200 rounded-lg p-1 bg-white">
                  <button
                    onClick={() => setView('grid')}
                    className={`p-1.5 rounded ${view === 'grid' ? 'bg-amber-500 text-white' : 'text-neutral-500 hover:text-neutral-800'}`}
                  >
                    <Grid3x3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setView('list')}
                    className={`p-1.5 rounded ${view === 'list' ? 'bg-amber-500 text-white' : 'text-neutral-500 hover:text-neutral-800'}`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
                <div className="relative">
                  <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    className="appearance-none border border-neutral-300 bg-white rounded-lg pl-3 pr-8 py-2 text-sm outline-none focus:border-amber-500 cursor-pointer"
                  >
                    <option value="default">Sortare implicită</option>
                    <option value="newest">Cele mai recente</option>
                    <option value="price-asc">Preț: de la mic la mare</option>
                    <option value="price-desc">Preț: de la mare la mic</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400 pointer-events-none" />
                </div>
              </div>
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20 bg-neutral-50 rounded-2xl border border-dashed border-neutral-300">
                <p className="text-neutral-500">Nu am găsit produse care să corespundă criteriilor tale.</p>
              </div>
            ) : view === 'grid' ? (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filtered.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {filtered.map((p) => (
                  <div key={p.id} className="flex gap-4 bg-white border border-neutral-200 rounded-xl p-4 hover:shadow-md transition">
                    <Link to={`/produs/${p.slug}`} className="shrink-0">
                      <img src={p.image} alt={p.name} className="w-32 h-32 object-cover rounded-lg bg-neutral-50" />
                    </Link>
                    <div className="flex-1">
                      <span className="text-xs uppercase tracking-wider text-amber-600">{p.categoryName}</span>
                      <Link to={`/produs/${p.slug}`}>
                        <h3 className="font-medium text-neutral-900 hover:text-amber-700 mt-1">{p.name}</h3>
                      </Link>
                      <p className="text-sm text-neutral-500 mt-1 line-clamp-2">{p.description}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <div>
                          {p.oldPrice && <span className="text-xs text-neutral-400 line-through mr-2">{p.oldPrice} lei</span>}
                          <span className="text-lg font-bold text-neutral-900">{p.price.toLocaleString('ro-RO')} lei</span>
                        </div>
                        <Link
                          to={`/produs/${p.slug}`}
                          className="text-sm font-medium px-4 py-2 rounded-full bg-neutral-900 hover:bg-amber-500 text-white transition"
                        >
                          Vezi detalii
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Shop;
